import {
  selectOrganizationId,
  useUpdateTourDetailsMutation,
} from '@carboncopyco/data';
import {
  AspectRatioOutlineIcon,
  BackOutlineIcon,
  BalloonIcon,
  ForwardOutlineIcon,
  ResponsiveOutlineIcon,
} from '@carboncopyco/icons';
import { isDesktop } from '@carboncopyco/mimesis-shared';
import type { FrameLoading, Workspace } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Navbar,
  Spacer,
  TextBase,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type RefObject, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTourDispatch, useTourSelector } from '../../hooks/store';
import {
  selectCanFinishCapturing,
  selectFramesLoading,
  selectIsCaptureButtonDisabled,
  selectIsCaptureStarted,
  selectIsCapturingFinished,
  selectIsReadyToCapture,
  selectIsShowingDimensionPanel,
  selectTourId,
  setIsShowingDimensionPanel,
  setTourRecordingFinished,
} from '../../store';
import { useDimensionsContext } from './DimensionsContext';
import RestartCaptureFramesModal from './RestartCaptureFramesModal';
import { SaveTourCaptureTooltip } from './SaveTourCaptureTooltip';
import { useTourCapturePageProps } from './TourCapturePageContext';

interface TourCaptureNavbarProps {
  onDiscard: () => void;
  demoId: string;
  entityName: string;
  originalTourId?: string;
  workspaceId?: string;
  workspaces?: Workspace[];
  isFirstFrameCaptured: boolean;
  url?: string;
  isOpenStartTourCaptureModal: boolean;
  containerRef?: RefObject<HTMLElement>;
}

export const TourCaptureNavbar = ({
  onDiscard,
  demoId,
  entityName,
  originalTourId,
  workspaceId,
  workspaces,
  isFirstFrameCaptured,
  url,
  isOpenStartTourCaptureModal,
  containerRef,
}: TourCaptureNavbarProps) => {
  const {
    uuid,
    useNavigate,
    WorkspaceSelectionField,
    desktopNavigate,
    onRestartCapturing,
    activeTabId,
    desktopHooks,
  } = useTourCapturePageProps();
  const { trackEvent } = useAnalytics();
  const { navigateToTourEditor } = useNavigate();
  const { state } = useLocation();
  const { folderId: folderIdFromParams } = useParams();
  const dispatch = useTourDispatch();
  const tabManager = desktopHooks?.useTabManager();
  const isShowingDimensionPanel = useTourSelector(
    selectIsShowingDimensionPanel(uuid)
  );
  const [updateTourDetails] = useUpdateTourDetailsMutation();
  const organizationId = useSelector(selectOrganizationId);
  const isReadyToCapture = useTourSelector(selectIsReadyToCapture(uuid));
  const isCaptureButtonDisabled = useTourSelector(
    selectIsCaptureButtonDisabled(uuid)
  );
  const isCaptureStarted = useTourSelector(selectIsCaptureStarted(uuid));
  const isCapturingFinished = useTourSelector(selectIsCapturingFinished(uuid));
  const canFinishCapturing = useTourSelector(selectCanFinishCapturing(uuid));
  const framesLoading = useTourSelector(selectFramesLoading(uuid));
  const tourId = useTourSelector(selectTourId(uuid));

  const doneCapturingButtonRef = useRef<HTMLButtonElement>(null);
  const doneCapturingButtonWrapperRef = useRef<HTMLDivElement>(null);
  const [openRestoreModal, setOpenRestoreModal] = useBoolean(false);
  const [showSaveCaptureModal, setShowSaveCaptureModal] = useBoolean(false);
  const _flags = useFlags();
  const { width, height, type, setWidth, setHeight } = useDimensionsContext();

  const dimensionRef = useRef(null);

  useEffect(() => {
    if (showSaveCaptureModal) {
      setShowSaveCaptureModal.setFalse();
    }
  }, [activeTabId]);

  useEffect(() => {
    if (!isCaptureStarted || originalTourId || type !== 'responsive') {
      return;
    }

    function handleResize() {
      const containerRect = containerRef.current.getBoundingClientRect();

      setWidth(containerRect.width);
      setHeight(containerRect.height);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [
    containerRef,
    isCaptureStarted,
    setHeight,
    setWidth,
    type,
    originalTourId,
  ]);

  const someFramesLoading = framesLoading.some(
    (frame: FrameLoading) => frame.isLoading
  );

  const isTourFromUrl = !demoId;
  const isFinishCapturingButtonDisabled =
    framesLoading.length === 0 ||
    someFramesLoading ||
    isCapturingFinished ||
    !canFinishCapturing ||
    !isFirstFrameCaptured;

  const restartCapturing = () => {
    onRestartCapturing?.();

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_RESTART_CAPTURE, {
      location: 'tour_capture_navbar',
      tourId,
      demoId,
      numberOfAddedFrames: framesLoading?.length || 0,
    });

    if (!isDesktop) {
      window.location.reload();
    }
  };

  const stopCapturing = () => {
    originalTourId
      ? trackEvent(AnalyticsTrackEventName.TOUR_CLICK_ADD_FRAME_FINISH, {
          location: 'add_frame_tour_capture',
          tourId,
          demoId,
          numberOfAddedFrames: framesLoading?.length || 0,
        })
      : trackEvent(AnalyticsTrackEventName.TOUR_CLICK_FINISH_CAPTURE, {
          location: 'tour_capture_drawer',
          tourId,
          demoId,
        });
    dispatch(setTourRecordingFinished({ uuid, isFinished: true }));
  };

  const folderId = state?.folderId ?? folderIdFromParams;

  const saveTour = ({ tourName, description, workspaceId }) => {
    const screenResolution = {
      width,
      height,
      type,
    };
    updateTourDetails({
      organizationId,
      id: tourId,
      tourName,
      description,
      workspaceId,
      draftSettings: {
        responsiveness: type === 'responsive',
        smallScreenWarning: true,
        showTooltips: true,
        showStepCounter: false,
        responsivenessDisabled: false,
        navigationToggle: false,
        screenResolution,
      },
      ...(folderId ? { folderId } : {}),
    })
      .unwrap()
      .then(() => {
        stopCapturing();
        setShowSaveCaptureModal.setFalse();
      });
  };

  const onTourFinish = () => {
    if (originalTourId) {
      stopCapturing();
    } else {
      setShowSaveCaptureModal.setTrue();
    }
  };

  const handleDimensions = () => {
    dispatch(
      setIsShowingDimensionPanel({
        uuid,
        isShowingDimensionPanel: !isShowingDimensionPanel,
      })
    );
  };

  const isCapturingDisabled =
    isOpenStartTourCaptureModal || !isReadyToCapture || isCaptureButtonDisabled;

  return (
    <Navbar>
      <Navbar.Icon
        icon={
          <BalloonIcon
            size={30}
            color={ColorHex.natural50}
          />
        }
        bgColor="bg-ds-accent1-300"
      />
      <Navbar.PageDetails title={entityName} />
      <Navbar.Separator />
      {isDesktop && (
        <div className="mx-2.5 flex gap-3 items-center overflow-hidden">
          <Navbar.Button
            variant="link"
            disabled={!desktopNavigate?.canGoBack}
            icon={<BackOutlineIcon />}
            onClick={desktopNavigate?.goBack}
            tooltipMessage="Backward"
          />
          <Navbar.Button
            variant="link"
            disabled={!desktopNavigate?.canGoForward}
            icon={<ForwardOutlineIcon />}
            onClick={desktopNavigate?.goForward}
            tooltipMessage="Forward"
          />
        </div>
      )}
      {url && (
        <div className="w-ds-46 truncate text-ds-natural">
          <TextBase
            label={url}
            ellipsis
          />
        </div>
      )}
      <div className="flex-grow" />
      <Navbar.ButtonGroup>
        {!originalTourId && !isDesktop && (
          <>
            <Navbar.Button
              label="Exit"
              variant="link"
              onClick={onDiscard}
            />
            <Spacer amount={0.5} />
          </>
        )}
        <div>
          {isCaptureStarted || !!originalTourId ? (
            <div className="flex flex-row items-center mr-2">
              <ResponsiveOutlineIcon color={ColorHex.natural300} />
              <div ref={dimensionRef}>
                <TextBase
                  label={
                    type === 'responsive' && !!originalTourId
                      ? 'Responsive'
                      : `${width} x ${height}px`
                  }
                />
              </div>
              <Tooltip
                placement="left"
                anchorRef={dimensionRef}
                shadow="md"
                offset={[0, 30]}
                noArrow
              >
                <div className="p-3">
                  <TextCaption
                    label="Tour's screen size."
                    color="text-ds-natural-300"
                    tagName="p"
                  />
                </div>
              </Tooltip>
            </div>
          ) : (
            <div className="mr-2">
              <Button
                onClick={handleDimensions}
                disabled={isCapturingDisabled}
                label="Screen size"
                blue
                link
                leftIcon={
                  <AspectRatioOutlineIcon
                    color={
                      isCapturingDisabled
                        ? ColorHex.natural200
                        : ColorHex.primary300
                    }
                  />
                }
              />
            </div>
          )}
        </div>
        {(!isTourFromUrl || (isDesktop && originalTourId)) && (
          <Navbar.Button
            label={originalTourId ? 'Cancel' : 'Restart'}
            variant="secondary"
            disabled={
              isDesktop
                ? originalTourId
                  ? false
                  : !isCaptureStarted
                : !isCaptureStarted
            }
            onClick={() => {
              if (originalTourId) {
                trackEvent(
                  AnalyticsTrackEventName.TOUR_CLICK_OPEN_CANCEL_MODAL,
                  {
                    location: 'add_frame_tour_capture',
                    tourId,
                    demoId,
                  }
                );
              }

              if (isCaptureStarted) {
                setOpenRestoreModal.setTrue();
                return;
              }

              tabManager?.closeGlobalActiveTab?.();
              navigateToTourEditor({
                id: originalTourId,
                workspaceId,
              });
            }}
          />
        )}
        <div ref={doneCapturingButtonWrapperRef}>
          <Navbar.Button
            ref={doneCapturingButtonRef}
            onClick={onTourFinish}
            disabled={isFinishCapturingButtonDisabled}
            label={originalTourId ? 'Finish & add frames' : 'Finish capturing'}
          />
        </div>

        {someFramesLoading && (
          <Tooltip
            placement={isDesktop ? 'left' : 'bottom'}
            anchorRef={doneCapturingButtonWrapperRef}
            offset={[0, 15]}
            shadow="md"
            noArrow={!!isDesktop}
          >
            <div className="p-3">
              <TextCaption
                label="Some frames are still loading"
                color="text-ds-natural-300"
                tagName="p"
              />
            </div>
          </Tooltip>
        )}
      </Navbar.ButtonGroup>
      {originalTourId ? (
        <RestartCaptureFramesModal
          open={openRestoreModal}
          onConfirm={() => {
            trackEvent(AnalyticsTrackEventName.TOUR_CLICK_APPROVE, {
              location: 'add_frame_tour_capture',
              tourId,
              demoId,
            });
            navigateToTourEditor({
              id: originalTourId,
              workspaceId,
            });
            setOpenRestoreModal.setFalse();
            tabManager?.closeGlobalActiveTab?.();
          }}
          onClose={() => {
            trackEvent(AnalyticsTrackEventName.TOUR_CLICK_CANCEL, {
              location: 'add_frame_tour_capture',
              tourId,
              demoId,
            });
            setOpenRestoreModal.setFalse();
          }}
          title="Are you sure?"
          description="This action will delete new captured frames"
          confirmButtonLabel="Yes, I'm sure"
          showTrashIcon={false}
        />
      ) : (
        <RestartCaptureFramesModal
          open={openRestoreModal}
          onConfirm={() => {
            restartCapturing();
            setOpenRestoreModal.setFalse();
          }}
          onClose={setOpenRestoreModal.setFalse}
          title="Discard captured frames"
          description="Are you sure you want to discard the current frames captured and start over?"
          confirmButtonLabel="Discard"
          showTrashIcon={true}
        />
      )}
      <SaveTourCaptureTooltip
        open={showSaveCaptureModal}
        ref={doneCapturingButtonRef}
        workspaceId={workspaceId}
        workspaces={workspaces}
        tourName={entityName}
        isLoading={false}
        onSubmit={saveTour}
        onClose={() => {
          setShowSaveCaptureModal.setFalse();
        }}
        WorkspaceSelectionField={WorkspaceSelectionField}
      />
    </Navbar>
  );
};
